/**
 * GLSL of the Medium tier's pixel-art surfaces (lifted from the dungeon-floors
 * prototype's `dungeon-surfaces.js`). Composed once per surface kind by the
 * functions below, never edited afterwards: the kind and the rock variant are
 * compile-time constants (the prototype's per-kind programs, so the compiler
 * drops the other kinds' branches), and the warm-dirt line exists only in the
 * floor-slab program. `dqFogAt` (the unexplored share) is declared here and
 * defined by the radiance hook (`shaders/radiance.ts`).
 */

/** Surface kinds with their own program: ground/rock/cap (`stone` variants), timber, iron. */
export type SurfaceKind = 'stone' | 'rockmass' | 'boulder' | 'timber' | 'iron';

/** Compile-time kind: 0 stone, 1 timber, 2 iron. */
export const kindIndex = (kind: SurfaceKind): number => {
  switch (kind) {
    case 'stone':
    case 'rockmass':
    case 'boulder':
      return 0;
    case 'timber':
      return 1;
    case 'iron':
      return 2;
  }
};

/** Rock variant: 0 floor slabs and border rock, 1 merged rock masses, 2 instanced rubble. */
export const variantIndex = (kind: SurfaceKind): number => (kind === 'rockmass' ? 1 : kind === 'boulder' ? 2 : 0);

export const surfaceVertexHeader = 'varying vec3 dqPosition,dqNormal,dqBox,dqExt,dqLocalN,dqOrigin;varying float dqTopY;attribute float dqTop;';

/** After `worldpos_vertex`: world position, normal, the unit box coordinates and the top edge height. */
export const surfaceVertex = `
vec4 dqWorld=vec4(transformed,1.),dqTopP=vec4(transformed.x,dqTop,transformed.z,1.);vec3 dqN=normal;dqBox=transformed;dqExt=vec3(1.);dqLocalN=normal;dqOrigin=vec3(0.);
#ifdef USE_INSTANCING
  dqExt=vec3(length(instanceMatrix[0].xyz),length(instanceMatrix[1].xyz),length(instanceMatrix[2].xyz));
  dqOrigin=instanceMatrix[3].xyz;
  dqWorld=instanceMatrix*dqWorld;dqN=mat3(instanceMatrix)*dqN;dqTopP=instanceMatrix*dqTopP;
#endif
dqPosition=(modelMatrix*dqWorld).xyz;dqNormal=normalize(mat3(modelMatrix)*dqN);dqTopY=dqTop>90.?1e4:(modelMatrix*dqTopP).y;`;

/** Uniforms, varyings and helpers; `size` is the atlas size in texels. */
export const surfaceFragmentHeader = (kind: SurfaceKind, size: number): string => `
varying vec3 dqPosition,dqNormal,dqBox,dqExt,dqLocalN,dqOrigin;
varying float dqTopY;
uniform vec4 dqSurface,dqPix,dqSpeck,dqGroundWarm;
uniform vec3 dqSpeckWarm,dqSpeckCool,dqCapCool;
uniform float dqFogContrast;
const float dqKind=${kindIndex(kind)}.;const int dqVariant=${variantIndex(kind)};
uniform sampler2D dqAtlas;
uniform vec3 dqRamp[24],dqRef[3],dqRampN;
float dqFogAt(vec3 p);
float dqHash(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
float dqNoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(dqHash(i),dqHash(i+vec2(1.,0.)),f.x),mix(dqHash(i+vec2(0.,1.)),dqHash(i+vec2(1.)),f.x),f.y);}
float dqBayer(vec2 t){vec2 a=mod(t,2.),b=mod(floor(t*.5),2.);return (4.*mod(a.x*2.+a.y*3.,4.)+mod(b.x*2.+b.y*3.,4.)+.5)/16.;}
vec4 dqTexel(vec2 t){return texture2D(dqAtlas,(t+.5)/${size}.);}
// Tone 0..1 -> ramp level, ordered-dithered in texel space; macro is in levels.
vec3 dqPick(int slot,float tone,float macro,vec2 t){
  float n=(slot==0?dqRampN.x:slot==1?dqRampN.y:dqRampN.z)-1.;
  float level=clamp(floor(tone*n+macro+(dqBayer(t)-.5)*dqPix.y+.5),0.,n);
  return dqRamp[slot*8+int(level)]/(slot==0?dqRef[0]:slot==1?dqRef[1]:dqRef[2]);
}`;

/** Ground, cap, strata lip, strata faces and rubble: one texel coordinate, channel, slot and level shift per branch. */
const stone = (size: number): string => `
vec3 P=dqPosition;vec3 N=normalize(dqNormal);float D=dqPix.x;
float along=abs(N.x)>abs(N.z)?-P.z*sign(N.x):P.x*sign(N.z),alongWidth=fwidth(along*D);
if(dqVariant==2){
  vec3 An=abs(N);vec2 uv=An.y>=An.x&&An.y>=An.z?P.xz:An.x>=An.z?vec2(P.z,P.y):vec2(P.x,P.y);
  qT=floor(uv*D);qOff=vec2(floor(dqHash(floor(dqOrigin.xz*3.)+dqSurface.w)*${size}.));qMask=vec4(0.,0.,0.,1.);qA=.72;qB=.1;
  float contact=clamp((P.y-dqOrigin.y)/max(dqExt.y*.35,.02)+.6,0.,1.);
  qSlot=1;qLevel=N.y*.9-.5-(1.-contact)*1.3;
}else if(dqVariant==0&&P.y<.2&&N.y>.55){
  qT=floor(vec2(P.x,-P.z)*D);vec2 block=floor(qT/${size}.);qOff=floor(vec2(dqHash(block+dqSurface.w),dqHash(block.yx+41.))*${size}.);qMask=vec4(1.,0.,0.,0.);
  qNP=P.xz*.31*dqSurface.x+dqSurface.w;qNA=1.9*dqPix.z;qGround=dqSpeck.y;qDirt=1.;qSlot=0;
  float h=dqHash(qT*1.37+vec2(dqSurface.w,7.1));
  qMul=h<dqSpeck.x?dqSpeckWarm:h>1.-dqSpeck.x?dqSpeckCool:vec3(1.);
}else if(dqVariant==1&&N.y>.97&&dqTopY<1e3&&P.y>.6){
  qT=floor(vec2(P.x,-P.z)*D);qOff=vec2(17.,91.);qMask=vec4(0.,1.,0.,0.);qA=.6;qB=.2;qSlot=1;qLevel=.8;
}else if(N.y>=.5){
  qT=floor(vec2(P.x,-P.z)*D/dqSpeck.z);vec2 block=floor(qT/${size}.);qOff=floor(vec2(dqHash(block+dqSurface.w+5.),dqHash(block.yx+13.))*${size}.);qMask=vec4(0.,0.,0.,1.);
  qSlot=2;qNP=P.xz*.42*dqSurface.x+3.;qNA=.5*dqPix.z;qCap=1.;qA=1.2;qB=-.1;
}else{
  float cols=exp2(max(0.,ceil(log2(max(alongWidth,1e-3)/1.15))));
  float col=floor(along*D/cols)*cols;
  float dx=col/D*.21*dqSurface.y,di=floor(dx),df=fract(dx);df=df*df*(3.-2.*df);
  float dip=floor((mix(dqHash(vec2(di,dqSurface.w)),dqHash(vec2(di+1.,dqSurface.w)),df)-.5)*12.);
  qT=vec2(col,floor(P.y*D)+dip);qOff=vec2(floor(dqHash(vec2(floor(col/${size}.),dqSurface.w))*${size}.),0.);qMask=vec4(0.,1.,0.,0.);
  float below=(dqTopY-P.y)*D;
  qSlot=1;qNP=vec2(along*.3,P.y*.55)*dqSurface.x+5.;qNA=1.3*dqPix.z;qLevel=below<1.?1.8:below<2.?.9:0.;
  float side=smoothstep(.55,.85,abs(N.x));qA=1.-.5*side;qB=.25*side;qMul=vec3(1.-.4*side);
  float graze=1.-smoothstep(.12,.45,abs(dot(N,normalize(cameraPosition-P))));
  qA*=1.-.7*graze;qLevel-=2.*graze;qMul*=1.-.45*graze;
}`;

/** Instanced unit boxes: grain along the longest local axis, a one-texel dark outline and a lit upper bevel. */
const box = `
vec3 e=dqExt,b=dqBox,Ln=abs(dqLocalN);float D=dqPix.x;
int K=Ln.x>=Ln.y&&Ln.x>=Ln.z?0:Ln.y>=Ln.z?1:2;
int A=e.x>=e.y&&e.x>=e.z?0:e.y>=e.z?1:2;
int U=K==A?(A+1)%3:3-A-K,V=K==A?(A+2)%3:A;
vec3 c=b*e;float seed=floor(dqHash(dqOrigin.xz*7.13+dqOrigin.y*3.1)*256.);
float eu=(.5-abs(b[U]))*e[U]*D,ev=(.5-abs(b[V]))*e[V]*D,edge=min(eu,ev);
bool merged=all(lessThan(abs(e-1.),vec3(.001)));
float bevel=merged?0.:edge<1.?-1.7:(edge<2.&&(eu<ev?U:V)==1&&b.y>0.)?1.:0.;`;

const timber = `
qT=floor(vec2(c[U],c[V])*D)+vec2(seed,floor(seed*.37));qMask=vec4(0.,0.,1.,0.);qA=K==A?.62:1.;
float up=normalize(dqNormal).y>.7?.45:0.;
float rail=up>0.&&e.y<.07&&e[A]>.78&&e[A]<.95&&abs(e[2-A]-.15)<.012&&abs(abs(c[A])-.24)<.026+1.2/D?-1.6:0.;
float contact=e.y>.12?(1.-smoothstep(0.,.16,dqPosition.y))*(1.1+dqFog):0.;
qLevel=bevel+up+rail-contact;qNP=vec2(c[U]*1.7,c[V]*.9)+seed;qNA=.8*dqPix.z;`;

const iron = `
vec3 N=normalize(dqNormal);
qT=merged?floor(vec2(dqPosition.x+dqPosition.z,dqPosition.y)*D):floor(vec2(c[U],c[V])*D)+seed;
float tone=.3+(dqHash(qT+dqSurface.w)-.5)*.16;
if(N.y>.6)tone=merged?(dqTopY>.45&&dqTopY<2.?dqPix.w:dqPix.w*.55):1.;else if(!merged&&bevel>.5)tone=.72;else if(!merged&&bevel<0.)tone=.05;else if(merged)tone*=.6;
qB=tone;`;

const dirt = `
if(qDirt>.5&&dqGroundWarm.w>0.){float t=.2+.5*dqGroundWarm.w;qC=mix(qC,dqGroundWarm.rgb*dot(qC*dqRef[0],vec3(.2126,.7152,.0722))/dqRef[0],smoothstep(t+.06,t-.06,qN)*.8);}`;

/** After `color_fragment`: the surface albedo multiplies the diffuse colour (instance tint over the palette reference). */
export const surfaceFragment = (kind: SurfaceKind, size: number): string => {
  const index = kindIndex(kind);
  const body = index === 0 ? stone(size) : `${box}${index === 1 ? timber : iron}`;
  return `
float dqFog=dqFogAt(dqPosition);
int qSlot=0;vec2 qT=vec2(0.),qOff=vec2(0.),qNP=vec2(0.);vec4 qMask=vec4(0.);float qA=1.,qB=0.,qLevel=0.,qNA=0.,qGround=0.,qCap=0.,qDirt=0.;vec3 qMul=vec3(1.);
{${body}}
float qN=dqNoise(qNP),sparse=clamp((qN-.42)*3.2,0.,1.)*qGround;
float qK=dot(qMask,vec4(1.))>0.?1.-(1.-dqFogContrast)*dqFog:1.;
vec3 qC=dqPick(qSlot,.5+(clamp(dot(dqTexel(qT+qOff),qMask)*qA+qB,.4*sparse,1.-.4*sparse)-.5)*qK,qLevel+(qN-.5)*qNA*qK,qT);
if(dqKind<.5&&qCap>.5&&dqSpeck.w>0.){
  float cool=smoothstep(.62-dqSpeck.w*.5,.74-dqSpeck.w*.5,qN);
  qC=mix(qC,dqCapCool*dot(qC*dqRef[2],vec3(.2126,.7152,.0722))/dqRef[2],cool);
}
${kind === 'stone' ? dirt : ''}
diffuseColor.rgb*=qC*qMul;`;
};
